import { ApolloError, useApolloClient, useMutation } from '@apollo/client';
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Select as OrbitSelect,
} from '@apollo/orbit';
import React, { ChangeEvent, ComponentProps, useState } from 'react';

import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { format } from 'src/lib/format';
import {
  ServiceGraphOwnerCardFragment,
  ServiceGraphOwnerCardFragment_account,
  UserGraphOwnerCardFragment_memberships,
} from 'src/lib/graphqlTypes/types';
import { planNameMapper } from 'src/lib/planMappers';

import { TransferGraphOwnerMutation } from './graphOwnerCardQueries';

type Memberships = Array<UserGraphOwnerCardFragment_memberships>;

interface GraphOwnerCardProps {
  memberships: Memberships;
  setError: (e: ApolloError) => void;
  service: ServiceGraphOwnerCardFragment;
}
interface GraphOwnerModalProps extends GraphOwnerCardProps {
  hideModal: () => void;
  defaultOwner?: Account;
}
export const GraphOwnerModal = ({
  memberships,
  setError,
  hideModal,
  service: { id, account: owner },
  defaultOwner,
}: GraphOwnerModalProps) => {
  if (!owner) throw new Error('Current Graph has no owner Organization.');
  if (!memberships.length || !memberships[0]?.account)
    throw new Error('Current Graph has no Account to transfer to.');

  const client = useApolloClient();
  const [toAccount, setToAccount] = useState<Account>(memberships[0].account);
  const canTransfer = canTransferToAccount(defaultOwner ?? owner, toAccount);
  const [transferOwnerMutation, { loading }] = useMutation(
    TransferGraphOwnerMutation,
    {
      variables: { serviceId: id, toAccountId: toAccount.id },
      onCompleted: (data) => {
        client.cache.evict({
          id: client.cache.identify({
            __typename: owner.__typename,
            id: owner.id,
          }),
          fieldName: 'services',
        });
        client.cache.evict({
          id: client.cache.identify({
            __typename: data.service?.transfer?.account?.__typename,
            id: data.service?.transfer?.account?.id,
          }),
          fieldName: 'services',
        });
        client.cache.gc();
        hideModal();
      },
      onError: (e) => setError(e),
    },
  );
  const transferService = () => {
    if (toAccount.id === owner.id) {
      hideModal();
    } else if (canTransfer) {
      transferOwnerMutation();
    }
  };

  const ownerOptionsIdToAccount: {
    [key: string]: Account;
  } = memberships.reduce((options, { account }) => {
    return { ...options, [account.id]: account };
  }, {});

  const ownerCapabilities = owner?.currentPlan;
  const toAccountCapabilities = toAccount?.currentPlan;

  const handleOwnerChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const account = ownerOptionsIdToAccount[e.target.value];
    if (account) {
      setToAccount(account);
    }
  };

  const showDataLossAlert =
    canTransfer &&
    (ownerCapabilities?.maxRangeInDays || 0) >
      (toAccountCapabilities?.maxRangeInDays || 0);

  const showIncompatiblePlanAlert = !canTransfer;

  return (
    <Modal onClose={hideModal} size="xl" isCentered isOpen>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Transfer this graph to another organization</ModalTitle>
        </ModalHeader>
        <ModalBody className="flex flex-col gap-4">
          <p>
            Your graph’s billing and support are managed by the organization it
            is in.
          </p>
          <p>
            If you transfer this graph to another organization, the admins of
            the new organization will be able to see and edit its information
            and settings. The new organization will also be responsible for
            paying for costs incurred by this graph after the transfer.
          </p>
          {showDataLossAlert && (
            <Alert status="warning">
              <b>Warning:</b> This change may cause data loss. This graph will
              be subject to the data retention period of the {toAccount.name}'s
              account
              {toAccountCapabilities?.maxRangeInDays ? (
                <>
                  , which is {toAccountCapabilities?.maxRangeInDays}{' '}
                  {format.pluralize(
                    toAccountCapabilities?.maxRangeInDays,
                    'day',
                  )}
                  . Any usage data older than{' '}
                  {toAccountCapabilities?.maxRangeInDays}{' '}
                  {format.pluralize(
                    toAccountCapabilities?.maxRangeInDays,
                    'day',
                  )}{' '}
                  will be deleted.
                </>
              ) : (
                '.'
              )}
            </Alert>
          )}
          {showIncompatiblePlanAlert && (
            <Alert status="error">
              <b>{toAccount.name}</b> is on the{' '}
              {planNameMapper(
                toAccount.currentPlan.name,
                toAccount.currentPlan.tier,
                toAccount.currentPlan.kind,
              )}{' '}
              plan, and doesn’t allow transfers from{' '}
              <span className="capitalize">
                {planNameMapper(
                  (defaultOwner ?? owner).currentPlan?.name,
                  (defaultOwner ?? owner).currentPlan?.tier,
                  (defaultOwner ?? owner).currentPlan?.kind,
                )?.toLowerCase()}
              </span>{' '}
              plans.
            </Alert>
          )}
          <FormControl className="w-1/2">
            <FormLabel htmlFor="org-selection">
              <b>Choose another organization</b>
            </FormLabel>
            <Select
              onChange={handleOwnerChange}
              value={toAccount.id}
              id="org-selection"
              className="mt-1"
            >
              {memberships.map(({ account }) => (
                <option value={account.id} key={`owner-option-${account.id}`}>
                  {account.name}
                </option>
              ))}
            </Select>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={hideModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={loading || !canTransfer}
              onClick={transferService}
            >
              Transfer
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export function GraphOwnerCard({
  service,
  memberships,
  setError,
}: GraphOwnerCardProps) {
  const { id, account: owner } = service;
  if (!owner) throw new Error('Current Graph has no owner Organization.');
  const ownerOptions =
    memberships?.filter(({ account }) => account.id !== owner.id) || [];
  const [modalVisible, setModalVisible] = useState(false);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Graph owner</CardTitle>
          <CardDescription>
            This graph currently belongs to the <b>{owner.name}</b> organization
            and all {owner.name} org admins can see this graph and modify it.
          </CardDescription>
          {!owner.capabilities?.cloudGraphs && (
            <PermissionWarning
              graphId={id}
              graphPermissions="canDelete"
              childRenderer={(hasPermission) => (
                <Button
                  variant="destructive"
                  onClick={() => setModalVisible(true)}
                  isDisabled={!hasPermission || ownerOptions.length === 0}
                >
                  Transfer Graph
                </Button>
              )}
            />
          )}
        </CardHeader>
      </Card>

      {modalVisible && ownerOptions.length > 0 && (
        <GraphOwnerModal
          service={service}
          memberships={ownerOptions}
          setError={setError}
          hideModal={() => setModalVisible(false)}
        />
      )}
    </>
  );
}

const Select = ({
  className,
  children,
  disabled,
  onChange,
  value,
  ...otherProps
}: ComponentProps<typeof OrbitSelect>) => {
  const [selectValue, setSelectValue] = useState(value);
  const internalOnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectValue(e.target.value);
    if (onChange) onChange(e);
  };
  return (
    <OrbitSelect
      {...otherProps}
      className={className}
      isDisabled={disabled}
      onChange={internalOnChange}
      value={selectValue}
    >
      {children}
    </OrbitSelect>
  );
};

type Account = ServiceGraphOwnerCardFragment_account;

const canTransferToAccount = (
  fromAccount: Account,
  toAccount: Account,
): boolean =>
  !(fromAccount.capabilities?.cloudGraphs === true) &&
  !fromAccount.capabilities?.cloudGraphs &&
  !toAccount.capabilities?.cloudGraphs;
